/* What changed about a visit between two reads of the book, in words.
 *
 * The board used to say "An edited booking was matched back to its square" and
 * stop there, which tells the desk that something happened to somebody. Kevin,
 * 2026-10-19: say which appointment, and what the change was. This is the
 * "what": a visit as it was and as it is, compared line by line.
 *
 * The book reissues its line ids on every edit, so lines are paired by service
 * — the same service on the same visit, nearest start first — exactly the way
 * the board re-finds its own squares. A line with no partner was added or
 * removed; a pair whose service differs is never made, so a service changed in
 * place reads as one removed and one added, which is what it was.
 *
 * Pure. A visit is { clientName, cancelled, notes, services: [{ serviceId, name,
 * startsAt, durationMinutes, techs: [labels], options: [{ id, name }] }] };
 * `clock(ms)` formats a time the way the board does.
 */

function pairLines(before, after) {
  var left = (after || []).slice();
  var pairs = [], removed = [];
  (before || []).slice().sort(function (a, b) { return a.startsAt - b.startsAt; }).forEach(function (b) {
    var best = -1;
    left.forEach(function (a, i) {
      if (a.serviceId !== b.serviceId) return;
      if (best === -1 || Math.abs(a.startsAt - b.startsAt) < Math.abs(left[best].startsAt - b.startsAt)) best = i;
    });
    if (best === -1) { removed.push(b); return; }
    pairs.push([b, left[best]]);
    left.splice(best, 1);
  });
  return { pairs: pairs, removed: removed, added: left };
}

function names(list) { return (list || []).join(', '); }

function sameList(x, y) {
  var a = (x || []).slice().sort(), b = (y || []).slice().sort();
  return a.length === b.length && a.every(function (v, i) { return v === b[i]; });
}

/* Each change as a short phrase, in the order they would be read: the visit
 * first, then its lines in time order. Empty when nothing the desk would care
 * about moved (a note edited, say). */
export function describeVisitChange(before, after, clock) {
  var fmt = clock || function (ms) { return String(ms); };
  if (!before || !after) return [];
  var out = [];
  if (!before.cancelled && after.cancelled) return ['cancelled'];
  if (before.cancelled && !after.cancelled) out.push('restored');
  if (before.clientName && after.clientName && before.clientName !== after.clientName) {
    out.push('client now ' + after.clientName + ' (was ' + before.clientName + ')');
  }

  var p = pairLines(before.services, after.services);
  var lines = [];
  p.pairs.forEach(function (pair) {
    var b = pair[0], a = pair[1];
    var bits = [];
    if (a.startsAt !== b.startsAt) bits.push('moved ' + fmt(b.startsAt) + ' → ' + fmt(a.startsAt));
    if ((a.durationMinutes || 0) !== (b.durationMinutes || 0) && a.durationMinutes && b.durationMinutes) {
      bits.push(b.durationMinutes + ' → ' + a.durationMinutes + ' min');
    }
    if (!sameList(a.techs, b.techs)) {
      bits.push(a.techs && a.techs.length
        ? 'now with ' + names(a.techs) + (b.techs && b.techs.length ? ' (was ' + names(b.techs) + ')' : '')
        : 'no technician now (was ' + names(b.techs) + ')');
    }
    var had = (b.options || []).map(function (o) { return o.id; });
    var has = (a.options || []).map(function (o) { return o.id; });
    var plus = (a.options || []).filter(function (o) { return had.indexOf(o.id) === -1; });
    var minus = (b.options || []).filter(function (o) { return has.indexOf(o.id) === -1; });
    if (plus.length) bits.push(plus.map(function (o) { return o.name; }).join(', ') + ' added');
    if (minus.length) bits.push(minus.map(function (o) { return o.name; }).join(', ') + ' removed');
    if (bits.length) lines.push({ at: a.startsAt, text: a.name + ' ' + bits.join(', ') });
  });
  p.removed.forEach(function (b) { lines.push({ at: b.startsAt, text: b.name + ' at ' + fmt(b.startsAt) + ' removed' }); });
  p.added.forEach(function (a) {
    lines.push({ at: a.startsAt, text: a.name + ' added at ' + fmt(a.startsAt) +
      (a.techs && a.techs.length ? ' with ' + names(a.techs) : '') });
  });
  lines.sort(function (x, y) { return x.at - y.at; });
  out = out.concat(lines.map(function (l) { return l.text; }));
  // Last, and only when it is the whole of the news or part of it: an edit to
  // the visit's note is the one change that moves nothing on the board.
  if ((before.notes || '') !== (after.notes || '')) out.push('notes edited');
  return out;
}
